import { getCurrentWindow } from '@tauri-apps/api/window'

import { showConfirm } from '@/services/dialogService'
import { logService, LOG_CATEGORIES } from '@/services/logService'
import { isAppleMobile, isTauri } from '@/services/platformService'
import { leaveGuardCopy } from '@/services/syncCopy'

/**
 * Guard the native desktop window close while unsynced changes exist. Desktop-only by
 * nature: on iOS/Android the app is backgrounded rather than closed, and the
 * IndexedDB-persisted queue is the protection there.
 * The browser `beforeunload` guard is unreliable inside the Tauri webview, hence this handler.
 */
export async function setupTauriCloseGuard(
  getUnsyncedCount: () => number,
): Promise<(() => void) | null> {
  if (!isTauri() || isAppleMobile()) {
    return null
  }

  try {
    const appWindow = getCurrentWindow()
    return await appWindow.onCloseRequested(async (event) => {
      const unsyncedCount = getUnsyncedCount()
      if (unsyncedCount === 0) {
        return
      }

      event.preventDefault()
      const { title, message } = leaveGuardCopy(unsyncedCount, false)
      const confirmed = await showConfirm({
        title,
        message,
        confirmText: 'Close App',
        cancelText: 'Keep Syncing',
        variant: 'warning',
      })
      if (confirmed) {
        // The native close was already cancelled by preventDefault(), so destroy() is the only
        // way out from here. If it rejects (e.g. the ACL grant is missing) the window would be
        // stuck open with no user-visible reason — log it explicitly instead of leaking an
        // unhandled rejection. Requires `core:window:allow-destroy` in capabilities/default.json.
        try {
          await appWindow.destroy()
        } catch (error) {
          logService.error(LOG_CATEGORIES.SYNC, 'Failed to close app after confirmation', {
            error,
          })
        }
      }
    })
  } catch (error) {
    logService.error(LOG_CATEGORIES.SYNC, 'Failed to register Tauri close guard', { error })
    return null
  }
}

export async function setNativeFullscreen(enabled: boolean): Promise<void> {
  if (!isTauri()) {
    return
  }

  const appWindow = getCurrentWindow()
  if (isAppleMobile()) {
    await appWindow.setSimpleFullscreen(enabled)
    return
  }

  await appWindow.setFullscreen(enabled)
}

export async function isNativeFullscreen(): Promise<boolean> {
  if (!isTauri()) {
    return false
  }

  const appWindow = getCurrentWindow()
  return appWindow.isFullscreen()
}
